// ARCHIVO COMPARTIDO app/web — editar en ambos repos. Verificar con `npm run check:shared` (TemControlApp).
//
// ── Bolsa de horas ─────────────────────────────────────────────────────────
// Mismo cálculo en web y móvil: versionado de horario por fecha + descuento
// de vacaciones/días completos/compensaciones aprobados + festivos + epoch
// 2026 + exclusión del día en curso. Las ausencias POR HORAS se recuperan
// (no reducen la jornada esperada, ver makeBalanceDayResolver). Además, cada
// trabajador puede tener una FECHA DE INICIO DE CÓMPUTO (su "alta"): antes de
// ella no cuenta nada (ver COMPUTATION_START_REASON y computationStartKey).
//
// Convención de signo:
//   · positivo → la EMPRESA debe horas al TRABAJADOR (crédito a su favor).
//   · negativo → el TRABAJADOR debe horas a la empresa.
// Las unidades internas son MINUTOS para evitar errores de coma flotante.
// El backend usa horas (FLOAT). Convertimos en los bordes (helpers
// hoursToMinutes / minutesToHours).
//
// El balance total = sum(autoDeltas por día con fichaje) + sum(manualOffsets).
// Los autoDeltas se recomputan client-side desde events + workhours; los
// offsets manuales vienen del endpoint GET /api/hourBalance/:workerId.
//
// ZONA HORARIA: todo lo que depende del día o de la hora de reloj (agrupar
// fichajes por día, inicio del cómputo, corte en "ayer", día de la semana
// del horario, horas del slot) se calcula en
// la zona del TRABAJADOR (`timeZone`, src/lib/workerTime.js →
// timeZoneForWorker). Por defecto Europe/Madrid, NUNCA la del dispositivo:
// la misma bolsa en cualquier móvil o navegador.

import { EVENT_TYPES, APPROVAL_STATUS } from '../services/api';
import { makeSlotResolver } from './timeline';
import { isRejectedStatus } from './dayStatus';
import {
  DEFAULT_TIME_ZONE, dayKeyInZone, dayKeysBetween, addDaysToDayKey, todayInZone,
  minutesOfDayInZone, zonedDateTimeToInstant, isDayKey,
} from './workerTime';

// Normaliza un flag booleano de Person tal como puede llegar de distintas
// fuentes (login/AuthContext, listados crudos, importaciones): true/false,
// 1/0, "true"/"false", "1"/"0" o null/undefined. null/undefined → false,
// que es el `defaultValue` del modelo en backend (models/person.js) para
// overtimeAllowed y enforceStartHour.
const flagOn = (v) => v === true || v === 1 || v === '1' || v === 'true';

// Inicio de la bolsa (decisión de producto): los días anteriores no computan.
export const BALANCE_EPOCH_ISO = '2026-01-01';

// ── Fecha de inicio de cómputo por trabajador (alta) ──────────────────────
// Desde esa fecha cuentan sus horas; TODO lo anterior no computa (ni jornada
// esperada, ni faltas, ni trabajado, ni vacaciones). Es el "alta" a efectos
// de bolsa, no el alta laboral real.
//
// ALMACENAMIENTO: el modelo Person del backend NO tiene campo de fecha (y
// descarta las claves desconocidas), así que la fecha se guarda como una FILA
// MARCADORA en la tabla HourBalance (/api/hourBalance):
//   { workerId, date: '<fecha de alta>', offset: 0, reason: 'Inicio de computo' }
// `reason` va en ASCII SIN TILDES a propósito (el body viaja seguro por el
// endpoint). La UI muestra el texto con tilde (COMPUTATION_START_LABEL).
//
// La fila NUNCA es un ajuste de horas: offset 0 y, además, se excluye de
// activeManualOffsets / sumManualOffsets (igual que los offsets antiguos de
// ausencias por horas). Se muestra como dato informativo en la bolsa, no
// como ajuste manual.
//
// Si por lo que sea hay VARIAS filas marcadoras (edición interrumpida, dos
// admins a la vez), gana la de fecha MÁS RECIENTE y la UI ofrece limpiar las
// sobrantes. Nunca se borra nada que no sea un marcador.
export const COMPUTATION_START_REASON = 'Inicio de computo'; // ASCII (body)
export const COMPUTATION_START_LABEL = 'Inicio de cómputo';  // UI
const COMPUTATION_START_RE = /^Inicio de computo/i;

export const isComputationStartOffset = (o) =>
  !!o && typeof o.reason === 'string' && COMPUTATION_START_RE.test(o.reason);

// La `date` de HourBalance puede llegar como 'YYYY-MM-DD' o como ISO completo
// (medianoche UTC). Nos quedamos con la clave de día tal cual se guardó.
export const offsetDayKey = (value) => {
  if (value == null) return null;
  const head = String(value).slice(0, 10);
  return isDayKey(head) ? head : null;
};

// Marcadores de inicio de cómputo ordenados por fecha DESC (el primero es el
// vigente; el resto son duplicados que la UI puede limpiar). Los marcadores
// con fecha ilegible se descartan.
export const computationStartOffsets = (offsets) =>
  (Array.isArray(offsets) ? offsets : [])
    .filter((o) => isComputationStartOffset(o) && offsetDayKey(o.date))
    .sort((a, b) => {
      const c = offsetDayKey(b.date).localeCompare(offsetDayKey(a.date));
      if (c !== 0) return c;
      return Number(b.id || 0) - Number(a.id || 0);
    });

// Fecha de inicio de cómputo vigente → 'YYYY-MM-DD' o null si no hay ninguna.
export const computationStartKey = (offsets) => {
  const list = computationStartOffsets(offsets);
  return list.length ? offsetDayKey(list[0].date) : null;
};

// Inicio efectivo del cómputo: nunca antes del epoch de la bolsa.
const computationStartFloor = (computationStart) => (
  isDayKey(computationStart) && computationStart > BALANCE_EPOCH_ISO
    ? computationStart
    : BALANCE_EPOCH_ISO
);

// Rango de eventos que se carga para el cómputo: 1 ene del año pasado → 31
// dic del actual. Mismo rango en TODAS las pantallas (web y app). Importa: el
// fin del cómputo es min(último evento, ayer), así que cargar otro rango
// cambia el resultado.
// El año "en curso" es el de la zona del trabajador (`timeZone`).
const yearInZone = (now, timeZone) => {
  const key = dayKeyInZone(now, timeZone) || todayInZone(timeZone);
  return Number(key.slice(0, 4));
};

export const balanceEventsRange = (now = new Date(), timeZone = DEFAULT_TIME_ZONE) => {
  const year = yearInZone(now, timeZone);
  return { start: `${year - 1}-01-01`, end: `${year}-12-31` };
};

// Años de festivos que hay que pasar a computeAutoBalance: TODOS los que
// abarca el cómputo, desde max(año del epoch, inicio del rango de eventos)
// hasta el año en curso. Con solo el año actual, desde el 1-ene-2027 cada
// festivo de 2026 contaba como falta (expected = jornada completa).
export const balanceHolidayYears = (now = new Date(), timeZone = DEFAULT_TIME_ZONE) => {
  const to = yearInZone(now, timeZone);
  const from = Math.max(
    Number(BALANCE_EPOCH_ISO.slice(0, 4)),
    Number(balanceEventsRange(now, timeZone).start.slice(0, 4)),
  );
  const years = [];
  for (let y = Math.min(from, to); y <= to; y += 1) years.push(y);
  return years;
};

// Solo aplica a trabajadores que pueden hacer extras o picar antes.
// El resto no acumulan ni deben horas (el modelo no tiene sentido).
// Punto ÚNICO de la regla de elegibilidad: antes se comparaba
// `enforceStartHour === false`, así que el MISMO trabajador era elegible
// desde el login (AuthContext rellena `?? false`) y NO elegible desde un
// registro crudo con el campo a null/undefined (filas antiguas en BD).
// Ahora null/undefined se tratan como false (default del backend).
export const isHourBalanceEligible = (worker) => {
  if (!worker) return false;
  return flagOn(worker.overtimeAllowed) || !flagOn(worker.enforceStartHour);
};

// Alias histórico (lo usan varias pantallas en web y app).
export const eligibleForBalance = isHourBalanceEligible;

const HMS_TO_MIN = (hms) => {
  if (!hms) return null;
  const [h, m] = String(hms).slice(0, 5).split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
};

// @deprecated 2026-09-21 — NO USAR. Decisión de producto: NINGUNA pausa
// cuenta como trabajada; todas se restan (ver computeDayWork). Ninguna
// pantalla la importa ya; se conserva solo por compatibilidad de la API
// exportada (y su test). Se eliminará en la próxima limpieza.
//
// Minutos de PAUSAS que caen FUERA de la jornada laboral del trabajador.
// Regla ANTIGUA (mayo 2026, commit 4435681):
//   · Pausa con AMBOS extremos DENTRO de un mismo slot → cuenta como
//     tiempo trabajado (no se descuenta). Es una pausa "real" dentro de
//     jornada (un café, una llamada, etc.).
//   · Pausa con cualquier extremo FUERA del slot → NO cuenta y se resta.
//     Esto cubre dos casos:
//       1) Pausa entre los dos turnos del turno partido (típica pausa
//          para comer), aunque el trabajador la haya iniciado unos
//          minutos antes del fin del turno de mañana.
//       2) Pausa registrada totalmente fuera de horario.
// Esta función devuelve los MINUTOS que hay que RESTAR al total. Si
// `slot` es null (día de descanso), se descuentan TODAS las pausas.
//
// Criterio de límite del slot:
//   · Inclusivo en la entrada (PAUSE en in1:00 cuenta como dentro).
//   · Exclusivo en la salida (PAUSE en out1:00 cuenta como fuera).
// Las horas de reloj de la pausa se leen en la zona del trabajador
// (`timeZone`), igual que las del slot.
export const pauseMinutesOutsideJornada = (dayEvents, slot, timeZone = DEFAULT_TIME_ZONE) => {
  const sorted = [...(dayEvents || [])]
    .filter((e) => e.eventType === EVENT_TYPES.PAUSE_START || e.eventType === EVENT_TYPES.PAUSE_END)
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  if (sorted.length === 0) return 0;
  const in1 = slot ? HMS_TO_MIN(slot.in1) : null;
  const out1 = slot ? HMS_TO_MIN(slot.out1) : null;
  const in2 = slot ? HMS_TO_MIN(slot.in2) : null;
  const out2 = slot ? HMS_TO_MIN(slot.out2) : null;
  // Una pausa "está dentro" si AMBOS instantes caen dentro del MISMO
  // slot (mañana o tarde). Cruzar el límite del slot → fuera, aunque
  // el inicio caiga dentro por unos segundos.
  const isPauseWithinSlot = (startDate, endDate) => {
    const ms = minutesOfDayInZone(startDate, timeZone);
    const me = minutesOfDayInZone(endDate, timeZone);
    if (in1 != null && out1 != null && ms >= in1 && ms < out1 && me >= in1 && me < out1) return true;
    if (in2 != null && out2 != null && ms >= in2 && ms < out2 && me >= in2 && me < out2) return true;
    return false;
  };
  let mins = 0;
  let pStart = null;
  for (const e of sorted) {
    if (e.eventType === EVENT_TYPES.PAUSE_START) pStart = e.timestamp;
    else if (e.eventType === EVENT_TYPES.PAUSE_END && pStart) {
      const within = slot && isPauseWithinSlot(new Date(pStart), new Date(e.timestamp));
      if (!within) {
        mins += Math.max(0, Math.floor((new Date(e.timestamp) - new Date(pStart)) / 60000));
      }
      pStart = null;
    }
  }
  return mins;
};

// Duración esperada del slot (mañana + tarde) en minutos.
// Si solo hay un turno, devuelve esa duración. Si el slot no existe (descanso),
// devuelve 0 — los días de descanso no cuentan para el balance auto (se
// asume que cualquier fichaje en descanso ya entra como compensación o
// extra explícita gestionada en aprobaciones).
const expectedMinutesForSlot = (slot) => {
  if (!slot) return 0;
  const in1 = HMS_TO_MIN(slot.in1);
  const out1 = HMS_TO_MIN(slot.out1);
  const in2 = HMS_TO_MIN(slot.in2);
  const out2 = HMS_TO_MIN(slot.out2);
  const dur1 = in1 != null && out1 != null ? Math.max(0, out1 - in1) : 0;
  const dur2 = in2 != null && out2 != null ? Math.max(0, out2 - in2) : 0;
  return dur1 + dur2;
};

// ── ¿Tiene el trabajador ALGÚN horario? ───────────────────────────────────
// Decisión de producto: sin horario ninguno la bolsa NO se calcula. Antes
// cada día contaba como descanso (expected = 0) y todo lo fichado salía como
// extra (+1175h falsas en producción). Se devuelve un flag y la UI muestra
// "Sin horario asignado" en lugar de un número.
//
// Acepta uno o varios inputs de horario (true si CUALQUIERA tiene jornada):
//   · Array de ScheduleVersion (histórico, enriquecido o no) → mira los
//     assignSlots de cada versión.
//   · Array plano de slots (workhours actuales, legacy).
//   · Map<dow, slot> u objeto indexado por dow.
//   · Función resolver → no se puede inspeccionar: se asume que SÍ hay.
//   · null / undefined / [] → no hay.
// Un slot solo cuenta si tiene horas (in1-out1 o in2-out2 con duración > 0):
// las filas de los días de descanso llegan con las horas a null.
// Uso típico: hasAnySchedule(enrichedScheduleHistory, workhours).
const slotHasHours = (slot) => !!slot && expectedMinutesForSlot(slot) > 0;
export const hasAnySchedule = (...inputs) => inputs.some((input) => {
  if (input == null) return false;
  if (typeof input === 'function') return true;
  if (Array.isArray(input)) {
    return input.some((item) => (item && Array.isArray(item.assignSlots)
      ? item.assignSlots.some(slotHasHours)
      : slotHasHours(item)));
  }
  if (typeof input.values === 'function') return Array.from(input.values()).some(slotHasHours);
  if (typeof input === 'object') return Object.values(input).some(slotHasHours);
  return false;
});

// ── Fichajes rechazados ───────────────────────────────────────────────────
// Decisión de producto: la bolsa EXCLUYE los eventos de tiempo de trabajo
// (entrada / salida / pausas) RECHAZADOS y sigue contando los PENDIENTES
// hasta que se aprueben o rechacen. approvalStatus null/undefined cuenta como
// aprobado (fichajes "en directo"). Misma regla que el calendario
// (dayStatus.buildDayStatusSets: un fichaje rechazado NO es día trabajado),
// así la bolsa y el calendario no se contradicen.
const WORK_TIME_EVENT_TYPES = new Set([
  EVENT_TYPES.CLOCK_IN, EVENT_TYPES.CLOCK_OUT,
  EVENT_TYPES.PAUSE_START, EVENT_TYPES.PAUSE_END,
]);
export const isRejectedWorkTimeEvent = (e) =>
  !!e && WORK_TIME_EVENT_TYPES.has(e.eventType) && isRejectedStatus(e.approvalStatus);

// ── Tiempo trabajado de UN día (punto ÚNICO) ──────────────────────────────
// Decisión de producto (2026-09-21): NINGUNA pausa cuenta como tiempo
// trabajado. TODAS las pausas se restan, en TODAS las pantallas (bolsa,
// dashboard, detalle del trabajador, informes, PDF/Excel). Sustituye a la
// regla de mayo en la que las pausas dentro de la jornada contaban como
// trabajadas (ver pauseMinutesOutsideJornada, obsoleta).
//
// computeDayWork(dayEvents, { timeZone, unit, now }) recorre los eventos del
// día (ya agrupados por día en la zona del trabajador) en orden cronológico
// con una máquina de estados  fuera → dentro → en pausa:
//   · Se DESCARTAN los fichajes/pausas RECHAZADOS (approvalStatus 2). Los
//     pendientes, aprobados y null cuentan (misma regla que la bolsa).
//   · CLOCK_IN  fuera          → empieza a contar.
//     CLOCK_IN  dentro         → se ignora; se conserva la PRIMERA entrada
//                                (comportamiento histórico de la bolsa).
//     CLOCK_IN  en pausa       → cierra la pausa ahí y reanuda el trabajo.
//   · PAUSE_START dentro       → para el reloj y abre una pausa.
//     PAUSE_START fuera        → se ignora (aviso 'pauseStartWhileOut').
//     PAUSE_START en pausa     → se ignora (aviso 'duplicatePauseStart').
//   · PAUSE_END en pausa       → cierra la pausa y reanuda el trabajo.
//     PAUSE_END fuera/dentro   → se ignora (aviso 'orphanPauseEnd').
//   · CLOCK_OUT dentro         → cierra el tramo de trabajo.
//     CLOCK_OUT en pausa       → la pausa termina en ese CLOCK_OUT (aviso
//                                'pauseClosedByClockOut') y se sale.
//     CLOCK_OUT fuera          → se ignora (aviso 'orphanClockOut').
//   · Entrada abierta al final del día → NO cuenta como trabajado
//     (openClockIn: true). Con `now` (temporizador en directo) cuenta hasta
//     `now`, solo si `now` cae en el MISMO día (zona `timeZone`) que la
//     entrada abierta. Igual con una pausa abierta (openPause: true).
// Cada tramo se redondea hacia abajo por separado (minutos o segundos).
//
// Devuelve:
//   { workedMinutes, pauseMinutes, workedSeconds, pauseSeconds,
//     worked, pause,            ← en la unidad pedida (unit 'minutes'|'seconds')
//     firstIn, lastOut,         ← timestamps (tal cual llegan) o null
//     openClockIn, openPause,   ← booleanos (estado al final del día)
//     intervals: [{ start, end, kind: 'work'|'pause', open }],  ← ms
//     warnings:  [{ type, event }],  ← anomalías (ver arriba + 'openPause')
//     hasPauseWarning }         ← true si alguna anomalía es de pausas
const TYPE_IN = EVENT_TYPES.CLOCK_IN;
const TYPE_OUT = EVENT_TYPES.CLOCK_OUT;
const TYPE_PSTART = EVENT_TYPES.PAUSE_START;
const TYPE_PEND = EVENT_TYPES.PAUSE_END;
const PAUSE_WARNING_TYPES = new Set([
  'pauseStartWhileOut', 'duplicatePauseStart', 'orphanPauseEnd', 'pauseClosedByClockOut', 'openPause',
]);

export const isPauseEvent = (e) => !!e && (e.eventType === TYPE_PSTART || e.eventType === TYPE_PEND);

export const computeDayWork = (dayEvents, { timeZone = DEFAULT_TIME_ZONE, unit = 'minutes', now = null } = {}) => {
  const seq = (Array.isArray(dayEvents) ? dayEvents : [])
    .filter((e) => e && WORK_TIME_EVENT_TYPES.has(e.eventType) && !isRejectedStatus(e.approvalStatus))
    .map((e) => ({ e, t: new Date(e.timestamp).getTime() }))
    .filter((x) => Number.isFinite(x.t))
    .sort((a, b) => a.t - b.t);

  const intervals = [];
  const warnings = [];
  let state = 'out'; // 'out' | 'in' | 'paused'
  let workStart = null;
  let pauseStart = null;
  let pauseEvent = null;
  let firstIn = null;
  let lastOut = null;

  const closeWork = (end) => {
    intervals.push({ start: workStart, end, kind: 'work', open: false });
    workStart = null;
  };
  const closePause = (end) => {
    intervals.push({ start: pauseStart, end, kind: 'pause', open: false });
    pauseStart = null;
    pauseEvent = null;
  };

  for (const { e, t } of seq) {
    switch (e.eventType) {
      case TYPE_IN:
        if (state === 'out') {
          workStart = t;
          state = 'in';
          if (firstIn == null) firstIn = e.timestamp;
        } else if (state === 'paused') {
          closePause(t);
          workStart = t;
          state = 'in';
          warnings.push({ type: 'clockInWhilePaused', event: e });
        } else {
          warnings.push({ type: 'duplicateClockIn', event: e });
        }
        break;
      case TYPE_PSTART:
        if (state === 'in') {
          closeWork(t);
          pauseStart = t;
          pauseEvent = e;
          state = 'paused';
        } else {
          warnings.push({ type: state === 'paused' ? 'duplicatePauseStart' : 'pauseStartWhileOut', event: e });
        }
        break;
      case TYPE_PEND:
        if (state === 'paused') {
          closePause(t);
          workStart = t;
          state = 'in';
        } else {
          warnings.push({ type: 'orphanPauseEnd', event: e });
        }
        break;
      case TYPE_OUT:
        if (state === 'in') {
          closeWork(t);
          state = 'out';
          lastOut = e.timestamp;
        } else if (state === 'paused') {
          warnings.push({ type: 'pauseClosedByClockOut', event: pauseEvent });
          closePause(t);
          state = 'out';
          lastOut = e.timestamp;
        } else {
          // Salida sin entrada: no cuenta nada (aviso 'orphanClockOut').
          warnings.push({ type: 'orphanClockOut', event: e });
        }
        break;
      default:
        break;
    }
  }

  // Estado abierto al final del día.
  const openClockIn = state !== 'out';
  const openPause = state === 'paused';
  if (openPause) warnings.push({ type: 'openPause', event: pauseEvent });
  if (openClockIn) {
    const nowMs = now == null ? NaN : new Date(now).getTime();
    const openStart = openPause ? pauseStart : workStart;
    const sameDay = Number.isFinite(nowMs)
      && dayKeyInZone(nowMs, timeZone) === dayKeyInZone(openStart, timeZone);
    if (sameDay && nowMs > openStart) {
      intervals.push({ start: openStart, end: nowMs, kind: openPause ? 'pause' : 'work', open: true });
    }
  }

  let workedMinutes = 0;
  let pauseMinutes = 0;
  let workedSeconds = 0;
  let pauseSeconds = 0;
  for (const iv of intervals) {
    const ms = Math.max(0, iv.end - iv.start);
    if (iv.kind === 'work') {
      workedMinutes += Math.floor(ms / 60000);
      workedSeconds += Math.floor(ms / 1000);
    } else {
      pauseMinutes += Math.floor(ms / 60000);
      pauseSeconds += Math.floor(ms / 1000);
    }
  }
  const inSeconds = unit === 'seconds';
  return {
    workedMinutes,
    pauseMinutes,
    workedSeconds,
    pauseSeconds,
    worked: inSeconds ? workedSeconds : workedMinutes,
    pause: inSeconds ? pauseSeconds : pauseMinutes,
    firstIn,
    lastOut,
    openClockIn,
    openPause,
    intervals,
    warnings,
    hasPauseWarning: warnings.some((w) => PAUSE_WARNING_TYPES.has(w.type)),
  };
};

// Variante en segundos (informes PDF/Excel).
export const computeDayWorkSeconds = (dayEvents, opts = {}) => computeDayWork(dayEvents, { ...opts, unit: 'seconds' });

// Eventos de un aviso de pausa (para marcar la fila en los historiales).
// Devuelve un Map<evento, tipo de aviso>.
export const pauseWarningsByEvent = (work) => {
  const out = new Map();
  for (const w of (work && work.warnings) || []) {
    if (w.event && PAUSE_WARNING_TYPES.has(w.type)) out.set(w.event, w.type);
  }
  return out;
};

// Texto corto del aviso de pausa (mismo en app y web).
export const PAUSE_WARNING_LABELS = {
  pauseStartWhileOut: 'Pausa sin entrada',
  duplicatePauseStart: 'Pausa duplicada',
  orphanPauseEnd: 'Fin de pausa sin inicio',
  pauseClosedByClockOut: 'Pausa sin cerrar (termina en la salida)',
  openPause: 'Pausa sin cerrar',
};

// Etiquetas de las filas de pausa en los historiales.
export const PAUSE_START_LABEL = 'Inicio pausa';
export const PAUSE_END_LABEL = 'Fin pausa';

// Agrupa eventos por día en la zona del trabajador y calcula cada día con
// computeDayWork. Devuelve Map<'YYYY-MM-DD', { events, work }> con TODOS los
// eventos del día (incluidos rechazados, para pintarlos) — el cómputo ya
// descarta los rechazados.
export const computeWorkByDay = (events, opts = {}) => {
  const timeZone = opts.timeZone || DEFAULT_TIME_ZONE;
  const byDay = new Map();
  for (const e of Array.isArray(events) ? events : []) {
    if (!e) continue;
    const key = dayKeyInZone(e.timestamp, timeZone);
    if (!key) continue;
    if (!byDay.has(key)) byDay.set(key, { events: [] });
    byDay.get(key).events.push(e);
  }
  for (const d of byDay.values()) d.work = computeDayWork(d.events, { ...opts, timeZone });
  return byDay;
};

// Minutos trabajados de un día para la bolsa. Idéntico a computeDayWork (la
// bolsa ya restaba TODAS las pausas). Única diferencia con el algoritmo
// anterior: un PAUSE_END huérfano con el trabajador FUERA ya no reabre el
// reloj (antes contaba hasta el siguiente CLOCK_OUT / PAUSE_START).
const workedMinutesForDay = (dayEvents, timeZone = DEFAULT_TIME_ZONE) =>
  computeDayWork(dayEvents, { timeZone }).workedMinutes;


// Variante razzia para railway: además de iterar el rango completo y descontar
// vacaciones/ausencias aprobadas, RESUELVE EL SLOT DEL DÍA contra la
// `workhoursOrHistory` recibida — puede ser:
//   · Map<dow, slot> o array plano legacy → mismo horario para todo el histórico.
//   · Array de ScheduleVersion[] con assignSlots, validFrom, validTo →
//     cada día se resuelve contra la versión vigente en esa fecha. Eso
//     arregla el caso "antes 38h/sem, ahora 40h/sem": los días pre-cambio
//     cuentan contra 38h y los post-cambio contra 40h, sin deuda falsa.
// Devuelve los intervalos [startTs, endTs] del slot en un día concreto.
// Hasta 2 intervalos (mañana + tarde). Útil para intersectar el slot con
// el rango de una ausencia por horas.
// `dayKey` es 'YYYY-MM-DD' y las horas del slot son de reloj en la zona del
// trabajador (`timeZone`).
const slotIntervalsForDay = (slot, dayKey, timeZone) => {
  if (!slot) return [];
  const build = (h1, h2) => {
    if (!h1 || !h2) return null;
    const trim = (s) => String(s).slice(0, 5);
    const s = zonedDateTimeToInstant(dayKey, trim(h1), timeZone).getTime();
    const e = zonedDateTimeToInstant(dayKey, trim(h2), timeZone).getTime();
    if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return null;
    return [s, e];
  };
  const out = [];
  const i1 = build(slot.in1, slot.out1);
  const i2 = build(slot.in2, slot.out2);
  if (i1) out.push(i1);
  if (i2) out.push(i2);
  return out;
};

const intersectIntervalsMinutes = (intervals, winStart, winEnd) => {
  let total = 0;
  for (const [s, e] of intervals) {
    const a = Math.max(s, winStart);
    const b = Math.min(e, winEnd);
    if (b > a) total += Math.floor((b - a) / 60000);
  }
  return total;
};

// ── Compensación: DÍA COMPLETO vs TRAMO HORARIO ───────────────────────────
// Un par 8/9 (COMPENSATION_START / COMPENSATION_END) aprobado puede ser de
// dos clases, y NO significan lo mismo:
//
//   · DÍA COMPLETO  → el admin le regala el día entero al trabajador.
//                     expected = 0: no tiene nada que fichar. Comportamiento
//                     histórico, no cambia.
//   · POR TRAMOS    → el trabajador sale antes (o entra después) y esas horas
//                     se marcan como COMPENSADAS. Decisión de producto
//                     ("Día −1h, bolsa −1h"): las horas compensadas NO son
//                     tiempo trabajado y NO reducen la jornada esperada. El
//                     día sale en déficit y la bolsa baja en esas horas —
//                     que es exactamente lo que se quiere: el trabajador está
//                     GASTANDO horas que tenía a su favor. Lo único que
//                     aporta el tramo es la JUSTIFICACIÓN: el día queda
//                     etiquetado como compensado y deja de contar como
//                     incidencia / "no llega".
//
// REGLA (punto ÚNICO, app y web) — el par es de DÍA COMPLETO si:
//   · termina en un día POSTERIOR al de inicio (rango de varios días), o
//   · su FIN cae en el minuto de cierre del día (>= 23:59 de reloj del
//     trabajador), que es la marca de "hasta el final del día".
// Cualquier otro par (horas reales dentro del día) es POR TRAMOS.
//
// Lo que decide es el FIN, NUNCA el inicio. Así conviven sin ambigüedad:
//   · los días completos HISTÓRICOS, creados 00:00 → 23:59, y
//   · los NUEVOS, creados COMPENSATION_FULL_DAY_START_HM → …_END_HM (23:58 →
//     23:59, ver abajo por qué el inicio ya no puede ser 00:00).
// Y los tramos reales (p. ej. 15:00 → 16:00) quedan fuera. La UI que crea
// tramos NO permite terminar a las 23:59 justamente para no colarse aquí.
//
// ── Por qué el día completo empieza a las 23:58 y no a las 00:00 ──────────
// El backend (Temcontrol-back routes/pikachuRouter.js) rechaza con 400
// "Cannot add event before person's start hour with enforceStartHour = TRUE"
// cualquier evento cuya hora sea anterior a la hora de entrada del
// trabajador, y solo EXIME a los tipos 2 (inicio vacaciones) y 4 (inicio día
// completo). El tipo 8 NO está exento, así que un inicio de compensación a
// las 00:00 era imposible para cualquier trabajador con enforceStartHour.
// Además esa comprobación lee el horario con
// `findOne({ where: { workerId, dayOfWeek } })`, SIN filtrar por versión de
// horario, así que puede comparar contra un slot ANTIGUO: no se puede elegir
// una hora "segura" mirando el horario vigente.
// 23:58 es seguro porque la comparación del backend es LÉXICA sobre
// `Date.toTimeString()` ("23:58:00 GMT+0200 …"), y ninguna hora de entrada
// real ordena por encima de "23:58". El par sigue cayendo entero en el MISMO
// día de la zona del trabajador, así que la clave de día, el calendario, los
// contadores de vacaciones y estos flags de periodo no cambian ni un bit.
export const COMPENSATION_FULL_DAY_START_HM = '23:58';
export const COMPENSATION_FULL_DAY_END_HM = '23:59';
// Último minuto del día: el fin de un día completo cae aquí.
const END_OF_DAY_MINUTE = 23 * 60 + 59;

// ¿El par [inicio, fin] de compensación cubre el DÍA COMPLETO? Ver la regla
// completa arriba. Si alguna marca de tiempo es ilegible se trata como día
// completo (comportamiento histórico: cualquier 8/9 daba expected 0).
export const isFullDayCompensationRange = (startTs, endTs, timeZone = DEFAULT_TIME_ZONE) => {
  const startKey = dayKeyInZone(startTs, timeZone);
  const endKey = dayKeyInZone(endTs, timeZone);
  if (!startKey || !endKey) return true;
  if (endKey > startKey) return true;
  const endMinute = minutesOfDayInZone(endTs, timeZone);
  return !Number.isFinite(endMinute) || endMinute >= END_OF_DAY_MINUTE;
};

// A partir de los eventos APROBADOS de vacaciones / día completo / por horas /
// compensación, construye un mapa por día con los flags necesarios para que
// el cálculo de la bolsa sepa qué hacer con cada fecha:
//   · onVacation     → ese día NO se espera que trabaje (expected=0).
//   · onFullDay      → idem (la deuda recuperable, si la hay, ya está como
//                      offset manual; no se duplica en el expected).
//   · onCompensation → día libre regalado por el admin (gift de un día).
//                      No se espera jornada, expected=0. SOLO los pares de
//                      DÍA COMPLETO (ver isFullDayCompensationRange).
//   · compensationIntervals → rangos [startTs, endTs] de las compensaciones
//                             POR TRAMOS que tocan el día. Se intersectan con
//                             el slot SOLO para informar de los minutos: NO
//                             reducen la jornada esperada ni cuentan como
//                             trabajadas (el déficit del día es el gasto de
//                             la bolsa).
//   · hourlyAbsenceIntervals → rangos [startTs, endTs] de las ausencias por
//                              horas que tocan el día. Se intersectan con
//                              el slot SOLO para informar de los minutos
//                              (las ausencias por horas se recuperan: no
//                              reducen la jornada esperada).
// Los días de cada periodo se deciden en la zona del trabajador (`timeZone`).
const buildPeriodFlagsByDay = (events, timeZone) => {
  const isApproved = (e) => Number(e.approvalStatus) === APPROVAL_STATUS.APPROVED;
  // Empareja START + END por orden cronológico, ignorando huérfanos.
  const pairRanges = (startType, endType) => {
    const list = (events || [])
      .filter((e) => isApproved(e) && (e.eventType === startType || e.eventType === endType))
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const out = [];
    let pending = null;
    for (const e of list) {
      if (e.eventType === startType) pending = e;
      else if (e.eventType === endType && pending) {
        out.push([new Date(pending.timestamp).getTime(), new Date(e.timestamp).getTime()]);
        pending = null;
      }
    }
    return out;
  };

  const vacationRanges = pairRanges(EVENT_TYPES.VACATION_START, EVENT_TYPES.VACATION_END);
  const fullDayRanges  = pairRanges(EVENT_TYPES.SICK_LEAVE_START, EVENT_TYPES.SICK_LEAVE_END);
  const hourlyRanges   = pairRanges(EVENT_TYPES.ABSENCE_START, EVENT_TYPES.ABSENCE_END);
  const compRanges     = pairRanges(EVENT_TYPES.COMPENSATION_START, EVENT_TYPES.COMPENSATION_END);
  // Día completo (regalo del día entero) vs tramo horario (horas sueltas).
  const compFullDayRanges = compRanges.filter(([s, e]) => isFullDayCompensationRange(s, e, timeZone));
  const compPartialRanges = compRanges.filter(([s, e]) => !isFullDayCompensationRange(s, e, timeZone));

  const flags = new Map();
  const ensure = (iso) => {
    if (!flags.has(iso)) {
      flags.set(iso, {
        onVacation: false, onFullDay: false, onCompensation: false,
        hourlyAbsenceIntervals: [], compensationIntervals: [],
      });
    }
    return flags.get(iso);
  };
  const markDays = (ranges, mark) => {
    for (const [s, e] of ranges) {
      for (const iso of dayKeysBetween(dayKeyInZone(s, timeZone), dayKeyInZone(e, timeZone))) {
        mark(ensure(iso), [s, e]);
      }
    }
  };
  markDays(vacationRanges, (d) => { d.onVacation = true; });
  markDays(fullDayRanges,  (d) => { d.onFullDay = true; });
  markDays(compFullDayRanges, (d) => { d.onCompensation = true; });
  markDays(compPartialRanges, (d, range) => { d.compensationIntervals.push(range); });
  markDays(hourlyRanges,   (d, range) => { d.hourlyAbsenceIntervals.push(range); });
  return flags;
};

// Resolver uniforme de slot por fecha a partir de cualquier formato de horario.
// Si el primer elemento tiene `dayOfWeek` directo → slots legacy. Si tiene
// `assignSlots` o `validFrom` → ScheduleVersion[] histórico. makeSlotResolver
// acepta ambos formatos + Map + función.
const buildSlotResolver = (workhoursOrHistory, timeZone) => {
  const inputIsLegacyFlat = Array.isArray(workhoursOrHistory)
    && workhoursOrHistory.length > 0
    && workhoursOrHistory[0]?.dayOfWeek != null
    && workhoursOrHistory[0]?.assignSlots == null;
  let resolverInput = workhoursOrHistory;
  if (inputIsLegacyFlat) {
    const m = new Map();
    for (const s of workhoursOrHistory) m.set(Number(s.dayOfWeek), s);
    resolverInput = m;
  }
  return makeSlotResolver(resolverInput, timeZone);
};

// ── Reglas de UN día de la bolsa (punto ÚNICO) ────────────────────────────
// makeBalanceDayResolver(events, horario, festivos, zona) → (iso) => info.
// Es exactamente la regla que aplica computeAutoBalance a cada día; la
// exponemos para que otras vistas (historial día a día de la ficha) calculen
// la jornada esperada con las MISMAS reglas y cuadren con la bolsa.
//   info = { date, slotMinutes, expected, isHoliday, onVacation, onFullDay,
//            onCompensation, hourlyAbsenceMinutes, partialCompensation,
//            compensationMinutes, beforeStart }
//   · Día ANTERIOR a la fecha de inicio de cómputo (`computationStart`) →
//     beforeStart: true y expected 0. Ese día no aporta NADA a la bolsa: ni
//     jornada esperada, ni falta, ni ausencia. Si hay fichajes, siguen ahí
//     (los historiales los pintan) pero fuera del cómputo.
//   · Festivo / vacaciones / día completo / compensación de DÍA COMPLETO
//     APROBADOS → expected 0.
//   · Compensación POR TRAMOS: igual que las ausencias por horas, NO toca
//     expected. El trabajador sale antes y el déficit del día es justo lo que
//     gasta de su bolsa (decisión de producto "Día −1h, bolsa −1h"). Se
//     devuelve `partialCompensation` y `compensationMinutes` (minutos del slot
//     que solapan los tramos) SOLO para etiquetar el día.
//   · Ausencias por horas: SIEMPRE se recuperan (decisión de producto
//     2026-09-22). NO reducen la jornada esperada: expected = slot completo,
//     así la hora de ausencia sale como deuda en el delta del día (y si el
//     trabajador no fichó la salida, el tiempo fichado la compensa sin
//     contarla dos veces). hourlyAbsenceMinutes (minutos del slot que solapan
//     las ausencias APROBADAS) se sigue calculando SOLO para mostrarlo
//     (tipo de día 'ausencia' en el historial).
//   · Si no, la jornada del horario vigente en esa fecha (0 si descanso).
// Los fichajes rechazados se descartan (no afectan a los periodos, que solo
// cuentan si están aprobados). Sin horario ninguno → devuelve null.
export const makeBalanceDayResolver = (events, workhoursOrHistory, holidaysSet = null, timeZone = DEFAULT_TIME_ZONE, computationStart = null) => {
  if (!hasAnySchedule(workhoursOrHistory)) return null;
  const evs = (Array.isArray(events) ? events : []).filter((e) => e && !isRejectedWorkTimeEvent(e));
  const resolveSlot = buildSlotResolver(workhoursOrHistory, timeZone);
  const periodFlags = buildPeriodFlagsByDay(evs, timeZone);
  const hasHolidays = holidaysSet && typeof holidaysSet.has === 'function';
  const startKey = isDayKey(computationStart) ? computationStart : null;
  return (iso) => {
    // Slot vigente en ESTA fecha (histórico → versión vigente en `iso`;
    // legacy → slot semanal por dayOfWeek).
    const slot = resolveSlot(iso);
    const slotMinutes = expectedMinutesForSlot(slot);
    const flag = periodFlags.get(iso);
    const isHoliday = !!(hasHolidays && holidaysSet.has(iso));
    const onVacation = !!(flag && flag.onVacation);
    const onFullDay = !!(flag && flag.onFullDay);
    const onCompensation = !!(flag && flag.onCompensation);
    // Compensación POR TRAMOS (horas sueltas): NO es día libre, solo
    // justifica esas horas. Ver isFullDayCompensationRange.
    const partialCompensation = !!(flag && flag.compensationIntervals.length > 0);
    // Antes del alta del trabajador: el día NO existe para la bolsa.
    const beforeStart = !!startKey && iso < startKey;
    let expected = slotMinutes;
    let hourlyAbsenceMinutes = 0;
    let compensationMinutes = 0;
    if (beforeStart) {
      expected = 0;
    } else if (isHoliday || onVacation || onFullDay || onCompensation) {
      // Festivos y días aprobados como vacaciones / día completo /
      // compensación de día completo no esperan jornada — si el trabajador
      // no fichó, no se le imputa como falta.
      expected = 0;
    } else if (slot && flag
      && (flag.hourlyAbsenceIntervals.length > 0 || flag.compensationIntervals.length > 0)) {
      // Solo informativo: ni la ausencia por horas (que se recupera) ni las
      // horas compensadas (que se pagan con la bolsa) tocan expected.
      const intervals = slotIntervalsForDay(slot, iso, timeZone);
      for (const [aS, aE] of flag.hourlyAbsenceIntervals) {
        hourlyAbsenceMinutes += intersectIntervalsMinutes(intervals, aS, aE);
      }
      for (const [cS, cE] of flag.compensationIntervals) {
        compensationMinutes += intersectIntervalsMinutes(intervals, cS, cE);
      }
    }
    return {
      date: iso, slotMinutes, expected, isHoliday, onVacation, onFullDay, onCompensation, hourlyAbsenceMinutes,
      partialCompensation, compensationMinutes,
      beforeStart,
    };
  };
};

// ── Periodo de cómputo de la bolsa (punto ÚNICO) ──────────────────────────
// { startKey, endKey } (claves de día en la zona del trabajador) o null si no
// hay nada que computar. Ver la explicación en computeAutoBalance (paso 3).
// `now` inyectable (tests); por defecto el reloj del sistema.
// `computationStart` = fecha de alta del trabajador ('YYYY-MM-DD' o null):
// el inicio es max(BALANCE_EPOCH_ISO, primer fichaje, fecha de alta), así que
// un fichaje anterior al alta NO arrastra el cómputo hacia atrás.
export const balanceComputationRange = (events, timeZone = DEFAULT_TIME_ZONE, now = Date.now(), computationStart = null) => {
  const evs = (Array.isArray(events) ? events : []).filter((e) => e && !isRejectedWorkTimeEvent(e));
  if (evs.length === 0) return null;
  const allTs = evs.map((e) => new Date(e.timestamp).getTime()).filter(Number.isFinite);
  const clockTs = evs
    .filter((e) => e.eventType === EVENT_TYPES.CLOCK_IN || e.eventType === EVENT_TYPES.CLOCK_OUT)
    .map((e) => new Date(e.timestamp).getTime())
    .filter(Number.isFinite);
  if (!clockTs.length) return null;
  const floorKey = computationStartFloor(computationStart);
  const firstClockKey = dayKeyInZone(Math.min(...clockTs), timeZone);
  const startKey = firstClockKey > floorKey ? firstClockKey : floorKey;
  const yesterdayKey = addDaysToDayKey(todayInZone(timeZone, now), -1);
  const lastEventKey = dayKeyInZone(Math.max(...allTs), timeZone);
  const endKey = lastEventKey < yesterdayKey ? lastEventKey : yesterdayKey;
  if (endKey < floorKey) return null;
  return { startKey, endKey };
};

// Calcula el balance automático iterando TODOS los días del rango
// [primer evento, último evento] y para cada uno:
//
//   · Si el día es de VACACIONES o DÍA COMPLETO aprobado → expected = 0.
//     La deuda de los "día completo" aprobados como recuperables ya queda
//     registrada en los offsets manuales; aquí no la duplicamos.
//   · Las AUSENCIAS POR HORAS se recuperan SIEMPRE (decisión 2026-09-22):
//     NO reducen expected. La hora ausente sale como deuda en el delta del
//     día (worked − jornada completa). Ya no se crean offsets manuales para
//     ellas, y los antiguos ("Por horas recuperable …") se ignoran en
//     sumManualOffsets para no descontarlas dos veces.
//   · Si no, expected = jornada completa del día (puede ser 0 si descanso).
//
//   delta(día) = worked - expected.
//
// IMPORTANTE: los días sin fichaje SÍ aportan ahora (no como antes). Si un
// día laborable no tiene fichaje ni justificación, queda como una falta
// (delta = -jornada). Es el comportamiento esperado.
//
// `holidaysSet` es opcional — un Set<string> de fechas ISO "YYYY-MM-DD"
// que son FESTIVOS para el trabajador (nacionales + autonómicos + locales).
// En esas fechas expected=0 igual que en vacaciones, así no se imputan
// como faltas. Si no se pasa, los festivos se tratan como días normales.
//
// Los fichajes / pausas RECHAZADOS se descartan ANTES de emparejar (y antes
// de anclar el inicio): un día con la entrada/salida rechazada queda como
// no trabajado (falta si era laborable). Los PENDIENTES siguen contando.
//
// Sin horario ninguno (hasAnySchedule === false) NO hay cómputo: devuelve
// { totalMinutes: null, byDay: [], noSchedule: true } y la UI muestra
// "Sin horario asignado" en vez de un número.
//
// `timeZone` = zona del trabajador (timeZoneForWorker). Días, "ayer", día de
// la semana y horas del slot se calculan en ella (por defecto Madrid).
//
// `computationStart` = fecha de INICIO DE CÓMPUTO del trabajador (su alta a
// efectos de bolsa, 'YYYY-MM-DD' o null). Los días anteriores no aportan
// NADA: ni jornada esperada, ni faltas, ni trabajado, ni vacaciones. Se
// obtiene de los offsets manuales con computationStartKey (computeBalanceTotals
// lo hace solo). Sin fecha → comportamiento de siempre.
//
// Devuelve { totalMinutes, byDay: [{date, expected, worked, delta}] }.
export const computeAutoBalance = (events, workhoursOrHistory, holidaysSet = null, timeZone = DEFAULT_TIME_ZONE, computationStart = null) => {
  if (!hasAnySchedule(workhoursOrHistory)) return { totalMinutes: null, byDay: [], noSchedule: true };
  const evs = (Array.isArray(events) ? events : []).filter((e) => e && !isRejectedWorkTimeEvent(e));
  if (evs.length === 0) return { totalMinutes: 0, byDay: [] };

  // 1. Indexar fichajes por día (en la zona del trabajador).
  const eventsByDay = new Map();
  for (const e of evs) {
    const iso = dayKeyInZone(e.timestamp, timeZone);
    if (!iso) continue;
    if (!eventsByDay.has(iso)) eventsByDay.set(iso, []);
    eventsByDay.get(iso).push(e);
  }

  // 2. Reglas por día (horario vigente + festivos + vacaciones / día
  //    completo / compensación / ausencias por horas aprobadas).
  const dayInfo = makeBalanceDayResolver(evs, workhoursOrHistory, holidaysSet, timeZone, computationStart);

  // 3. Rango de cómputo (balanceComputationRange):
  //    · Inicio: max(primer evento, BALANCE_EPOCH, fecha de alta). Por decisión de
  //      producto, la bolsa de horas SOLO considera fichajes desde
  //      2026-01-01 — los eventos anteriores se ignoran (caso típico:
  //      trabajadores que empezaron a fichar en 2025 con datos
  //      incompletos que falsearían el cómputo).
  //    · Fin: min(último evento, AYER 23:59). Clampeamos a AYER porque
  //      hoy aún puede no haberse fichado y los días futuros con eventos
  //      pre-creados (vacaciones programadas, fichajes anticipados)
  //      hacían aparecer los laborables intermedios como faltas hasta esa
  //      fecha. En cuanto pasen las 23:59 de hoy, el día se incorpora.
  //    Todo se decide con claves de DÍA en la zona del trabajador (no con
  //    medianoches del dispositivo).
  // El INICIO se ancla en el primer FICHAJE real (entrada/salida), NO en
  // cualquier evento. Si no, una vacación/baja registrada ANTES de que el
  // trabajador empiece a fichar arrastra el cómputo hasta esa fecha y le
  // imputa como falta todos los días laborables intermedios — días en los que
  // aún no usaba la app. (Caso José María: vacación suelta el 23-abr y primer
  // fichaje el 21-may → 19 días laborables = -159h fantasma.)
  // `evs` ya viene sin fichajes rechazados: uno rechazado no ancla el inicio.
  // Sin ningún fichaje real, o si todos los eventos son anteriores al epoch,
  // no hay nada que computar (range === null).
  const range = balanceComputationRange(evs, timeZone, Date.now(), computationStart);
  if (!range) return { totalMinutes: 0, byDay: [] };
  const { startKey, endKey } = range;

  const byDay = [];
  let totalMinutes = 0;
  for (const iso of dayKeysBetween(startKey, endKey)) {
    const dayEvents = eventsByDay.get(iso) || [];
    const worked = workedMinutesForDay(dayEvents, timeZone);
    const { expected } = dayInfo(iso);

    const delta = worked - expected;
    // No saturamos el detalle con días sin actividad ni jornada (festivos
    // del trabajador sin nada que mostrar). El total tampoco cambia porque
    // worked y expected son 0.
    if (worked > 0 || expected > 0) {
      byDay.push({ date: iso, expected, worked, delta });
      totalMinutes += delta;
    }
  }
  byDay.sort((a, b) => b.date.localeCompare(a.date));
  return { totalMinutes, byDay };
};

// ── Offsets antiguos de ausencias por horas (sin efecto) ──────────────────
// Hasta 2026-09-22, al aprobar una ausencia POR HORAS como "recuperable" la
// web creaba un offset manual negativo con motivo "Por horas recuperable
// (1h)". Ahora esas horas ya salen como deuda en el cómputo automático
// (expected = jornada completa), así que esos offsets la duplicarían. NO se
// borran de la BD: se ignoran al sumar y la UI los marca "sin efecto".
// Los de día completo ("Día completo recuperable …") siguen contando.
const SUPERSEDED_HOURLY_OFFSET_RE = /^Por horas recuperable/;
export const isSupersededHourlyOffset = (o) =>
  !!o && typeof o.reason === 'string' && SUPERSEDED_HOURLY_OFFSET_RE.test(o.reason);

// Offsets que SÍ cuentan en la bolsa (sin los "sin efecto" y sin los
// marcadores de inicio de cómputo, que son metadatos con offset 0 y NUNCA un
// ajuste de horas — ver COMPUTATION_START_REASON).
export const activeManualOffsets = (offsets) =>
  (Array.isArray(offsets) ? offsets : []).filter(
    (o) => o && !isSupersededHourlyOffset(o) && !isComputationStartOffset(o),
  );

// Suma los offsets manuales (en horas → minutos enteros). Ignora los
// offsets antiguos de ausencias por horas (isSupersededHourlyOffset).
export const sumManualOffsets = (offsets) => {
  if (!Array.isArray(offsets)) return 0;
  let total = 0;
  for (const o of activeManualOffsets(offsets)) {
    const h = Number(o?.offset);
    if (Number.isFinite(h)) total += Math.round(h * 60);
  }
  return total;
};

// Cómputo final de la bolsa (automático + ajustes manuales). Punto ÚNICO que
// usan la web (HourBalanceCard, chips de Equipo / Comparar, modal de
// aprobación, vía src/hooks/useWorkerHourBalance.js) y la app
// (WorkerDetailScreen), para que no puedan dar números distintos.
// Devuelve { noSchedule, auto, manual, total, byDay, manualCount,
//            computationStart }.
// La FECHA DE INICIO DE CÓMPUTO sale de los propios `manualOffsets`
// (computationStartKey): quien ya carga los ajustes manuales hereda el corte
// sin tocar nada. `computationStart` se devuelve para pintarlo como dato
// informativo en la tarjeta de bolsa (nunca como ajuste).
// Sin horario asignado → noSchedule: true, auto: null, total: null (NO es un
// número: la UI muestra "Sin horario asignado"); los ajustes manuales se
// siguen devolviendo aparte (`manual`) para enseñarlos si existen.
// `timeZone` = zona del trabajador (timeZoneForWorker); por defecto Madrid.
export const computeBalanceTotals = (events, scheduleInput, holidaysSet, manualOffsets, timeZone = DEFAULT_TIME_ZONE) => {
  const computationStart = computationStartKey(manualOffsets);
  const auto = computeAutoBalance(events, scheduleInput, holidaysSet, timeZone, computationStart);
  const manual = sumManualOffsets(manualOffsets);
  const manualCount = activeManualOffsets(manualOffsets).length;
  if (auto.noSchedule) {
    return { noSchedule: true, auto: null, manual, total: null, byDay: [], manualCount, computationStart };
  }
  return {
    noSchedule: false,
    auto: auto.totalMinutes,
    manual,
    total: auto.totalMinutes + manual,
    byDay: auto.byDay,
    manualCount,
    computationStart,
  };
};

// Formato corto: -1h 30m / +2h / 0h. Usa el signo siempre que no sea cero
// para que el usuario sepa de un vistazo a quién favorece el saldo.
export const formatBalance = (totalMinutes) => {
  const m = Math.round(Number(totalMinutes) || 0);
  if (m === 0) return '0h';
  const sign = m > 0 ? '+' : '−';
  const abs = Math.abs(m);
  const hh = Math.floor(abs / 60);
  const mm = abs % 60;
  if (hh === 0) return `${sign}${mm}m`;
  if (mm === 0) return `${sign}${hh}h`;
  return `${sign}${hh}h ${String(mm).padStart(2, '0')}m`;
};

// Color semántico para el balance: verde si la empresa debe (positivo,
// crédito a favor del trabajador), rojo si el trabajador debe, gris si 0.
export const balanceColor = (totalMinutes) => {
  const m = Number(totalMinutes) || 0;
  if (m > 0) return 'positive';
  if (m < 0) return 'negative';
  return 'neutral';
};

// ── Textos de "sin horario" (mismos en app y web) ─────────────────────────
export const NO_SCHEDULE_LABEL = 'Sin horario asignado';
export const NO_SCHEDULE_SHORT_LABEL = 'Sin horario'; // chips de listas
export const NO_SCHEDULE_ADMIN_HINT = 'Asigna un horario para calcular la bolsa';

// "Sin horario asignado" o, si hay ajustes manuales,
// "Sin horario asignado · ajustes manuales +3h 18m".
export const formatNoScheduleBalance = (manualMinutes) => {
  const m = Math.round(Number(manualMinutes) || 0);
  return m === 0 ? NO_SCHEDULE_LABEL : `${NO_SCHEDULE_LABEL} · ajustes manuales ${formatBalance(m)}`;
};

// Helper de paso para enviar al backend: minutos enteros → horas float.
export const minutesToHours = (m) => Math.round((Number(m) || 0)) / 60;
export const hoursToMinutes = (h) => Math.round((Number(h) || 0) * 60);
